import type { ComponentType } from 'react';
import {
  AlertCircleIcon,
  CheckCircleIcon,
  ClockIcon,
  MinusCircleIcon,
  XCircleIcon,
  type IconProps,
} from '@/components/icons';
import { StatusIndicator } from '@/components/StatusIndicator';
import { subtaskStatusMap, taskStatusMap, taskTypeMap } from '@/config/statusMaps';
import type { SubtaskStatus, Task } from '@/types/task';

/**
 * Task status pill
 */
export function TaskStatusIndicator({ status }: { status: string }) {
  return <StatusIndicator status={status} map={taskStatusMap} baseClassName="tasks-status" />;
}

/**
 * Task type badge
 */
export function TaskTypeBadge({ type }: { type: string }) {
  const config = taskTypeMap.get(type);
  return <span className={`tasks-type-badge ${config.className}`.trim()}>{config.label}</span>;
}

/**
 * Subtask status badge (lowercase label)
 */
export function SubtaskStatusBadge({ status }: { status: string }) {
  const config = subtaskStatusMap.get(status);
  return <span className={`tasks-subtask-status-badge ${config.className}`}>{config.label}</span>;
}

/**
 * Icon per subtask status; unknown values use the pending icon
 */
const SUBTASK_ICONS: Record<SubtaskStatus, ComponentType<IconProps>> = {
  completed: CheckCircleIcon,
  failed: XCircleIcon,
  running: ClockIcon,
  skipped: MinusCircleIcon,
  pending: AlertCircleIcon,
};

export function SubtaskStatusIcon({ status }: { status: string }) {
  const Icon = subtaskStatusMap.isKnown(status) ? SUBTASK_ICONS[status] : SUBTASK_ICONS.pending;
  const statusClass = subtaskStatusMap.isKnown(status) ? status : 'pending';
  return <Icon size={null} className={`tasks-subtask-icon tasks-subtask-icon-${statusClass}`} />;
}

/**
 * Subtask progress bar of a task
 */
export function TaskProgressBar({ task }: { task: Task }) {
  const total = task.total_subtasks;
  if (total === 0) return <span className="tasks-progress-text">-</span>;

  const completed = task.completed_subtasks;
  const failed = task.failed_subtasks;
  const pct = Math.round(((completed + failed) / total) * 100);

  let barClass = 'tasks-progress-bar-ok';
  if (failed > 0 && completed > 0) barClass = 'tasks-progress-bar-partial';
  else if (failed > 0 && completed === 0) barClass = 'tasks-progress-bar-fail';

  return (
    <div className="tasks-progress">
      <div className="tasks-progress-bar-container">
        <div className={`tasks-progress-bar ${barClass}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="tasks-progress-text">
        {completed}/{total}
        {failed > 0 && ` (${failed} failed)`}
      </span>
    </div>
  );
}
